import axios from 'axios';
import configuredAxios from '@/api/axiosConfig';
import { store } from '@/store/store';
import { ApiRoutes } from '../ApiRoutes';
import { IStandardResponseError } from '@/models/StandardResponse';
import { fileNameFromContentDisposition } from '@/services/fileDownload';
import { InviteColumns } from '@/constants/inviteColumns';
import {
  CsvHeadersResult,
  InviteAccountTypeOption,
  InviteAccountTypesResult,
  InviteApplicationOption,
  InviteApplicationsResult,
  InviteColumnMapping,
  InviteRow,
  LoadInvitesRequest,
  LoadInvitesResult,
  ParseInvitesResult
} from '@/types/adminInvites';

// MM-42243: SPA client for the bulk invite-load endpoints. Both are multipart uploads: the CSV rides as the `file`
// form field and (for :load) the batch params ride as sibling form fields, so we build a FormData and let the
// browser set the multipart boundary — we deliberately do NOT reuse the JSON-defaulted axios instance here, and we
// do NOT route through handleApiRequest.ts (it redirects on any non-422). The endpoints answer a 400 with a
// StandardResponse { errors: [{ key, localizedName }] } for whole-batch failures (missing/oversize/malformed/empty
// CSV, or unknown application key / country / account type on load); those are surfaced inline, so we translate a
// 400 of that shape into a typed AdminInvitesBatchError the caller can catch and display.

// A whole-batch rejection (HTTP 400) carrying the server's localized error list. Distinct from a per-row failure,
// which comes back inside a 200 LoadInvitesResult.
export class AdminInvitesBatchError extends Error {
  public readonly errors: IStandardResponseError[];

  constructor(errors: IStandardResponseError[]) {
    super(errors[0]?.localizedName ?? 'The request could not be processed.');
    this.name = 'AdminInvitesBatchError';
    this.errors = errors;
    // Restore the prototype chain so `instanceof` works after TS downlevels to ES5.
    Object.setPrototypeOf(this, AdminInvitesBatchError.prototype);
  }
}

// Re-throws a 400-with-StandardResponse as AdminInvitesBatchError; otherwise rethrows the original error unchanged
// (network failure, 401/403, 500, etc.) for the caller's generic handling.
function translateError(error: any): never {
  const data = error?.response?.data;
  if (error?.response?.status === 400 && Array.isArray(data?.errors))
    throw new AdminInvitesBatchError(data.errors as IStandardResponseError[]);
  throw error;
}

// Read a client CSV's column headers (schema-agnostic) to populate the column-mapping subform. Returns each column's
// original header text plus the camelCase key the server's DataWeaver loader emits for it. A 400 (missing/oversize/
// unreadable CSV) raises AdminInvitesBatchError.
export async function getCsvHeaders(file: File): Promise<CsvHeadersResult> {
  const form = new FormData();
  form.append('file', file, file.name);

  store.state.loadingCount++;
  try {
    const response = await axios.post(ApiRoutes.Admin.CsvHeaders, form);
    return response.data as CsvHeadersResult;
  } catch (error) {
    return translateError(error);
  } finally {
    store.state.loadingCount--;
  }
}

// Preview an uploaded CSV: returns editable grid rows plus the per-cell errors to highlight. No invites are created.
// When a column `mapping` is supplied (the client CSV does not use our canonical headers), it rides as a JSON form
// field; the server builds a DataWeaver transform from it to project the CSV onto our shape before previewing.
export async function parseInvitesCsv(file: File, mapping?: InviteColumnMapping): Promise<ParseInvitesResult> {
  const form = new FormData();
  form.append('file', file, file.name);
  if (mapping && Object.keys(mapping).length > 0)
    form.append('mapping', JSON.stringify(mapping));

  store.state.loadingCount++;
  try {
    const response = await axios.post(ApiRoutes.Admin.ParseInvites, form);
    return response.data as ParseInvitesResult;
  } catch (error) {
    return translateError(error);
  } finally {
    store.state.loadingCount--;
  }
}

// Commit a bulk load: serialize-to-CSV rows are posted as `file` with the batch params as sibling form fields. A 200
// LoadInvitesResult reports best-effort per-row outcomes (some may have failed); a 400 raises AdminInvitesBatchError.
export async function loadInvites(csv: string, params: LoadInvitesRequest): Promise<LoadInvitesResult> {
  const form = new FormData();
  form.append('file', new Blob([csv], { type: 'text/csv' }), 'invites.csv');

  // ApplicationKeys is a List<string>: repeat the field once per value so model binding rebuilds the list.
  params.applicationKeys.forEach(key => form.append('ApplicationKeys', key));
  if (params.name != null) form.append('Name', params.name);
  if (params.externalId != null) form.append('ExternalId', params.externalId);
  if (params.countryCode != null) form.append('CountryCode', params.countryCode);
  if (params.authPathSegment != null) form.append('AuthPathSegment', params.authPathSegment);
  form.append('AccountTypeName', params.accountTypeName);

  store.state.loadingCount++;
  try {
    const response = await axios.post(ApiRoutes.Admin.LoadInvites, form);
    return response.data as LoadInvitesResult;
  } catch (error) {
    return translateError(error);
  } finally {
    store.state.loadingCount--;
  }
}

// Export the grid's current rows as the canonical invite CSV. The rows go up as JSON with the column order — the grid's
// on-screen order (InviteColumns), its hidden columns last — and the server streams the CSV back in that order as a
// file download; the caller saves the returned Blob under the server's file name. A 400 (no rows) comes back
// as a Blob too, so it is read as JSON before translating it into an AdminInvitesBatchError.
export async function exportInvitesCsv(rows: InviteRow[]): Promise<{ blob: Blob; fileName: string }> {
  store.state.loadingCount++;
  try {
    const columns = InviteColumns.map(col => col.header);
    const response = await axios.post(ApiRoutes.Admin.ExportInvites, { rows, columns }, { responseType: 'blob' });
    const fileName = fileNameFromContentDisposition(response.headers['content-disposition']) ?? 'invites.csv';
    return { blob: response.data as Blob, fileName };
  } catch (error: any) {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      try {
        error.response.data = JSON.parse(await data.text());
      } catch {
        // Not JSON: leave the error as-is for the caller's generic handling.
      }
    }
    return translateError(error);
  } finally {
    store.state.loadingCount--;
  }
}

// Source App choices for the page's multi-select. Uses the configured (authenticated) axios instance — these are
// plain GET JSON lookups, unlike the multipart upload calls above.
export async function getInviteApplications(): Promise<InviteApplicationOption[]> {
  store.state.loadingCount++;
  try {
    const response = await configuredAxios.get(ApiRoutes.Admin.InviteApplications);
    return (response.data as InviteApplicationsResult).applications ?? [];
  } finally {
    store.state.loadingCount--;
  }
}

// Application Account Type choices (children of the "Applicaton Account Type" parent DataType): the tenant account
// type the load endpoint validates AccountTypeName against and each Source App must be configured for.
export async function getInviteAccountTypes(): Promise<InviteAccountTypeOption[]> {
  store.state.loadingCount++;
  try {
    const response = await configuredAxios.get(ApiRoutes.Admin.InviteAccountTypes);
    return (response.data as InviteAccountTypesResult).accountTypes ?? [];
  } finally {
    store.state.loadingCount--;
  }
}

// Per-user Account Type choices (children of the "ZBEdgeAccountType" parent DataType, e.g. ClinicalUser/ZBEmployee):
// a different vocabulary from the Application Account Type above, stamped into each row's AccountType cell and
// validated per row by the invite service.
export async function getInviteUserAccountTypes(): Promise<InviteAccountTypeOption[]> {
  store.state.loadingCount++;
  try {
    const response = await configuredAxios.get(ApiRoutes.Admin.InviteUserAccountTypes);
    return (response.data as InviteAccountTypesResult).accountTypes ?? [];
  } finally {
    store.state.loadingCount--;
  }
}
